import { PageHeader } from '@ant-design/pro-components';
import { Button, Input, Select, Table } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import startCase from 'lodash.startcase';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';
import { Refinance } from '../../../features/refinance/refinanceSlice';
import { numberWithCommas } from '../../../helpers/number';
import { getAllRefinancesThunk } from '../../../services/refinanceService/refinanceThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDateAndTime } from '../../../utils/datetime.util';

const { Search } = Input;

const SearchRefinancePage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { refinanceList } = useSelector((state: RootState) => state.refinance);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    [pageNum, pageSize]
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<Refinance>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('applicationNo');
  const [searchValue, setSearchValue] = useState('');

  const tablePagination = {
    total: refinanceList.data.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Created Date & Time',
      dataIndex: ['application', 'createdAt'],
      key: 'createdAt',
      sorter: (a, b) =>
        new Date(a.application?.createdAt).getTime() - new Date(b.application?.createdAt).getTime(),
      sortOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
      render: (text) => (text ? displayDateAndTime(text) : '-'),
    },
    {
      title: 'Refinance No.',
      dataIndex: ['application', 'applicationNo'],
      key: 'applicationNo',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/application/details/${record.application?.id}`)}
        >
          <span style={{ userSelect: 'text' }}>{text}</span>
        </Button>
      ),
    },
    {
      title: 'Applicant Name',
      dataIndex: ['application', 'applicantFirstName'],
      key: 'applicantFirstName',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: ['application', 'applicantNric'],
      key: 'applicantNric',
    },
    {
      title: 'Existing Outstanding Balance',
      dataIndex: ['totalLoanOutstanding'],
      key: 'totalLoanOutstanding',
      render: (text) => (text != null ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Status',
      dataIndex: ['application', 'applicationStatus'],
      key: 'applicationStatus',
      render: (text) => startCase(text ?? ''),
    },
  ];

  const fetchRefinance = async (params?: any) => {
    await dispatch(getAllRefinancesThunk(params));
  };
    
  const onSearch = (value: string) => {
    value = value.trim();
    if (value) {
      setSearchValue(value.toUpperCase());
      fetchRefinance({
        ...initialParams,
        filters: { ...filterState },
        search: { [searchFiled]: value.toUpperCase() },
      });
    } else {
      fetchRefinance({
        ...initialParams,
        filters: { ...filterState },
      });
    }

    setFilterState({ ...initialParams.filters });
  };

  const handleTableChange: TableProps<Refinance>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param = searchValue.length === 0
    ? {
        ...initialParams,
        ...filterState,
        pagination,
        sortField: sorter?.columnKey,
        sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
      }
    : {
        ...initialParams,
        ...filterState,
        pagination,
        sortField: sorter?.columnKey,
        sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
        search: {
          [searchFiled]: searchValue.toUpperCase(),
        },
      };

    fetchRefinance(param);
    setSortedInfo(sorter as SorterResult<Refinance>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    fetchRefinance(params);
  });

  return (
    <>
      <Helmet>
        <title>Search Refinance - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Search Refinance" />
      <Input.Group compact>
        <Select
          defaultValue={searchFiled}
          style={{ width: 200 }}
          onChange={setSearchFiled}
          options={[
            { value: 'applicationNo', label: 'Refinance No.' },
            { value: 'applicantFirstName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' }
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
        <Table
          rowKey="id"
          className="min-w-fit"
          columns={columns}
          pagination={tablePagination}
          dataSource={(refinanceList?.data?.data ?? [])}
          loading={refinanceList?.isLoading}
          onChange={handleTableChange}
        />
    </>
  );
};

export default SearchRefinancePage;
